import { Cancel, Download } from '@mui/icons-material';
import { Alert, Box, Button, Grid, IconButton, Typography, useMediaQuery } from '@mui/material';
import { useSnackbar } from 'notistack';
import { FC, useCallback, useContext, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { QueryObserverResult } from 'react-query';
// Fetch, Models, Context
import { UserContext } from '../../../../../context';
import {
  downloadContractDocument,
  replaceContractDocument,
  replaceFinalContract
} from '../../../../../fetch';
import { IUnderContractDocument, IUnderContractDocumentResults } from '../../../../../models';
// Helpers
import { FileDrop, Loader, MobileTable, Table } from '../../../../../components';
import {
  downloadFile,
  formatDate,
  getDownloadFileName,
  isAllowedToEditUnderContract
} from '../../../../../helpers';
import { FileHistoryModal } from './file-history-modal';

interface IContractFiles {
  contractDocuments: IUnderContractDocumentResults;
  isLoadingContractFiles: boolean;
  refetchContractDocumentResults: () => Promise<
    QueryObserverResult<IUnderContractDocumentResults, Error>
  >;
  buildId: string;
}

export const ContractFiles: FC<IContractFiles> = ({
  contractDocuments,
  isLoadingContractFiles,
  refetchContractDocumentResults,
  buildId
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const userContext = useContext(UserContext);
  const hasEditAccess = isAllowedToEditUnderContract(userContext.user);
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [downloading, setDownloading] = useState<Record<number, boolean>>({});
  const [isReplacing, setIsReplacing] = useState<Record<number, boolean>>({});
  const [isShowingDropzone, setIsShowingDropzone] = useState<Record<number, boolean>>({});
  const [selectedBuildDocument, setSelectedBuildDocument] = useState<IUnderContractDocument | null>(
    null
  );

  const handleDownloadDocument = useCallback(
    async (buildDocumentId: number, fileName: string) => {
      setDownloading(prev => ({ ...prev, [buildDocumentId]: true }));
      try {
        const res = await downloadContractDocument(buildDocumentId);
        downloadFile(res?.data, getDownloadFileName(res) || fileName);
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? `Error downloading document, please try again.`, {
          variant: 'error'
        });
      } finally {
        setDownloading(prev => ({ ...prev, [buildDocumentId]: false }));
      }
    },
    [enqueueSnackbar]
  );

  const handleSaveFinalContract = useCallback(
    async (finalContractData: any) => {
      try {
        const formData = new FormData();
        formData.append('FinalContract', finalContractData);

        await replaceFinalContract(buildId, formData);
        await refetchContractDocumentResults();

        enqueueSnackbar('Success, final contract uploaded!', {
          variant: 'success'
        });
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? `Error uploading final contract, please try again.`, {
          variant: 'error'
        });
        console.log(error);
      }
    },
    [buildId, enqueueSnackbar, refetchContractDocumentResults]
  );

  const handleSaveDocument = useCallback(
    async (file: File, buildDocumentId: number, buildDocumentTypeId: number) => {
      try {
        setIsReplacing(prev => ({ ...prev, [buildDocumentId]: true }));

        const formData = new FormData();
        formData.append('Document', file, file.name);
        await replaceContractDocument(buildId, buildDocumentTypeId, formData);
        await refetchContractDocumentResults();

        enqueueSnackbar('Success, document uploaded!', {
          variant: 'success'
        });
        setIsReplacing(prev => ({ ...prev, [buildDocumentId]: false }));
        setIsShowingDropzone(prev => ({ ...prev, [buildDocumentId]: false }));
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? `Error uploading document, please try again.`, {
          variant: 'error'
        });
        setIsReplacing(prev => ({ ...prev, [buildDocumentId]: false }));
      }
    },
    [buildId, enqueueSnackbar, refetchContractDocumentResults]
  );

  const handleClose = () => {
    setSelectedBuildDocument(null);
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Type',
        accessor: 'name',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IUnderContractDocument } };
        }) => {
          return <>{original?.name}</>;
        }
      },
      {
        Header: 'File Name',
        accessor: 'fileName',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IUnderContractDocument } };
        }) => {
          return (
            <Button
              disabled={downloading[original?.buildDocumentId]}
              onClick={() =>
                handleDownloadDocument(
                  original?.buildDocumentId,
                  original?.fileName ?? original?.name
                )
              }
              sx={theme => ({
                maxWidth: '20rem',
                [theme.breakpoints.down('sm')]: {
                  maxWidth: '16rem'
                },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              })}
            >
              <Download />{' '}
              {original?.fileName ? (
                <Typography
                  variant="body2"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%'
                  }}
                >
                  {original?.fileName}
                </Typography>
              ) : (
                <div>No file name available</div>
              )}
            </Button>
          );
        }
      },
      {
        Header: 'Date Uploaded',
        accessor: 'date',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IUnderContractDocument } };
        }) => {
          return original?.date ? (
            <Typography>{formatDate(original?.date)}</Typography>
          ) : (
            <Typography>No date available</Typography>
          );
        }
      },

      {
        Header: isMobile ? 'Actions' : '',
        accessor: 'actions',
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IUnderContractDocument } };
        }) => {
          const isReplacingForDocument = isReplacing[original.buildDocumentId];
          const isDropzoneVisible = isShowingDropzone[original.buildDocumentId];
          const isFinalContract = original.name === 'Final Contract';

          const {
            getRootProps,
            getInputProps,
            open,
            isFocused,
            isDragActive,
            isDragAccept,
            isDragReject,
            isFileDialogActive,
            fileRejections,
            rootRef,
            inputRef,
            acceptedFiles
          } = useDropzone({
            accept: {
              'application/pdf': ['.pdf']
            },
            useFsAccessApi: false,
            onDrop: acceptedFiles => {
              if (acceptedFiles.length > 0) {
                const acceptedFile = acceptedFiles[0];
                if (isFinalContract) {
                  handleSaveFinalContract(acceptedFile);
                } else {
                  handleSaveDocument(
                    acceptedFile,
                    original.buildDocumentId,
                    original.buildDocumentTypeId
                  );
                }
              } else {
                enqueueSnackbar(
                  `Invalid file type. Please upload one of these image types, .pdf.`,
                  {
                    variant: 'error'
                  }
                );
              }
            }
          });
          return (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <>
                {isDropzoneVisible ? (
                  <Grid item xs={12}>
                    {isReplacingForDocument ? (
                      <Loader type="inline" position="centered" /> // Loader during file replacement
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                          aria-label="hide dropzone"
                          onClick={() =>
                            setIsShowingDropzone(prev => ({
                              ...prev,
                              [original.buildDocumentId]: false
                            }))
                          }
                        >
                          <Cancel />
                        </IconButton>
                        <FileDrop
                          id="File-drop"
                          getRootProps={getRootProps}
                          getInputProps={getInputProps}
                          open={open}
                          isFocused={isFocused}
                          isDragActive={isDragActive}
                          isDragAccept={isDragAccept}
                          isDragReject={isDragReject}
                          isFileDialogActive={isFileDialogActive}
                          fileRejections={fileRejections}
                          rootRef={rootRef}
                          inputRef={inputRef}
                          acceptedFiles={acceptedFiles}
                          title=""
                        >
                          <Typography variant="body1">
                            Drag and drop a file or{' '}
                            <Box
                              component="a"
                              href="/#"
                              sx={{
                                color: theme => theme.palette.primary.main,
                                textDecoration: 'none'
                              }}
                              onClick={e => e.preventDefault()}
                            >
                              browse and pick
                            </Box>{' '}
                          </Typography>
                        </FileDrop>
                      </Box>
                    )}
                  </Grid>
                ) : (
                  <Button
                    disabled={!hasEditAccess}
                    sx={theme => ({
                      marginLeft: theme.spacing(1),
                      padding: theme.spacing(0.5), // Default padding
                      [theme.breakpoints.down('sm')]: {
                        fontSize: theme.spacing(0.75) // Reduced padding on mobile
                      }
                    })}
                    onClick={() =>
                      setIsShowingDropzone(prev => ({
                        ...prev,
                        [original.buildDocumentId]: true
                      }))
                    }
                    variant="contained"
                    color="primary"
                  >
                    {isReplacingForDocument ? 'Replacing...' : 'Replace File'}
                  </Button>
                )}
              </>
              <Button
                sx={theme => ({ marginLeft: theme.spacing(1) })}
                onClick={() => setSelectedBuildDocument(original)}
                variant="text"
                color="primary"
              >
                History
              </Button>
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    downloading,
    isReplacing,
    isShowingDropzone,
    handleDownloadDocument,
    hasEditAccess,
    isMobile,
    handleSaveDocument,
    handleSaveFinalContract
  ]);

  return (
    <>
      {isLoadingContractFiles && (
        <Box mt={1} minHeight="30rem" display="flex" flexDirection="column" justifyContent="center">
          <Loader type="inline" position="centered">
            Loading...
          </Loader>
        </Box>
      )}
      {!isLoadingContractFiles && (
        <Grid container spacing={1}>
          <Grid item xs={12}>
            {contractDocuments?.buildDocuments?.length !== 0 && (
              <Table
                columns={columns}
                data={contractDocuments?.buildDocuments}
                isLoading={isLoadingContractFiles}
                hidePagination
                mobileProps={{
                  fields: columns
                    .filter(c => c.Header)
                    .map(c => ({
                      name: c.Header,
                      accessor: c.accessor,
                      Cell: c?.Cell
                    }))
                }}
                ResponsiveComponent={isMobile ? MobileTable : undefined}
              />
            )}
            {contractDocuments?.buildDocuments?.length === 0 && (
              <Box marginBottom="8px" display="block">
                <Alert severity="warning">Files Not Uploaded</Alert>
              </Box>
            )}
            <FileHistoryModal
              fileNames={selectedBuildDocument ? [selectedBuildDocument.fileName] : []}
              documentName={selectedBuildDocument?.name}
              buildDocumentId={selectedBuildDocument?.buildDocumentId}
              onClose={handleClose}
            />
          </Grid>
        </Grid>
      )}
    </>
  );
};
